import styled, {css} from 'styled-components';

import {border, colors, font, transition} from '@app/styles/theme';

export const Wrapper = styled.div`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    background-color: ${colors.white};
    padding: 28px 16px 40px;
`;

export const Header = styled.div`
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: space-between;
    align-items: flex-start;
`;

export const Title = styled.h1`
    font-family: ${font.family.primary};
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 36px;
    color: ${colors.textPrimary};
    margin: 0;
    padding: 0;
`;

export const Description = styled.p`
    font-family: ${font.family.primary};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    color: ${colors.textSecondary};
    margin: 0;
    padding: 0;
`;

export const DaysSwitcherSection = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

export const DaysSwitcherContainer = styled.div`
    width: 100%;
    max-width: 520px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    border-radius: 16px;
    background: ${colors.white};
    box-shadow: 0 4px 15px 0 rgba(34, 62, 120, 0.14);
    overflow: hidden;
`;

export const DaysSwitcherRoot = styled.div`
    position: relative;
    width: 100%;
    max-width: 520px;
    height: 36px;
    background: ${colors.white};
    overflow: hidden;

    display: grid;
    grid-template-columns: repeat(4, 1fr);
`;

export const DaysSwitcherBackground = styled.div<{activeIndex: number}>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 25%;
    background: ${colors.brandPrimary};
    border-radius: 16px;
    transform: translateX(${(p) => p.activeIndex * 100}%);
    transition: transform ${transition.duration} ${transition.func};
`;

export const DayOptionButton = styled.button`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    color: ${colors.brandPrimary};
    border: 0;
    background: transparent;
    cursor: pointer;
    position: relative;
    z-index: 1;
    user-select: none;

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px ${colors.brandDark};
    }

    &.active {
        color: ${colors.white};
    }
`;

export const PlansGrid = styled.div<{$itemCount: number}>`
    width: 100%;
    max-width: 1204px;
    margin: 0 auto;
    display: grid;
    gap: 16px;

    ${(p) => {
        const n = p.$itemCount;
        if (n <= 0) {
            return css`
                grid-template-columns: 1fr;
            `;
        }
        if (n <= 3) {
            return css`
                grid-template-columns: repeat(${n}, minmax(260px, 400px));
                justify-content: center;
            `;
        }
        return css`
            grid-template-columns: repeat(3, minmax(260px, 1fr));
        `;
    }}

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        justify-content: stretch;
    }
`;

export const PlanCard = styled.div<{$active?: boolean; $popular?: boolean}>`
    box-sizing: border-box;
    border: 1px solid ${colors.border};
    border-radius: ${border.radius};
    background: ${colors.white};
    padding: 60px 40px 40px 40px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    text-align: left;
    height: fit-content;
    min-height: 330px;

    transition: border-color ${transition.duration} ${transition.func},
        box-shadow ${transition.duration} ${transition.func},
        transform ${transition.duration} ${transition.func},
        background-color ${transition.duration} ${transition.func};

    ${(p) =>
        p.$popular &&
        css`
            background: ${colors.brandLight};
            border-color: ${colors.brandPrimary};
        `}

    ${(p) =>
        p.$active &&
        !p.$popular &&
        css`
            // background: #eaf1ff;
        `}

    &:hover {
        border-color: ${colors.brandPrimary};
        box-shadow: 0 0 0 2px ${colors.brandPrimary}1A;
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px ${colors.brandDark}1A;
    }
`;

export const MostPopularBadge = styled.div`
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 1;

    padding: 6px 12px;
    border-radius: 999px;
    background: ${colors.brandPrimary};
    color: ${colors.white};

    font-family: ${font.family.primary};
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
`;

export const PlanHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    text-align: center;
`;

export const PlanInvoices = styled.div`
    font-family: ${font.family.primary};
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
    color: ${colors.textGrey1};
`;

export const PlanDescription = styled.div`
    font-family: ${font.family.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textSecondary};
`;

export const PlanPrice = styled.div`
    position: relative;
    width: fit-content;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 10px;
    margin: 40px 0;

    .price {
        font-family: ${font.family.primary};
        font-size: 34px;
        font-weight: 700;
        line-height: 40px;
        color: ${colors.black};
    }

    .suffix {
        font-family: ${font.family.primary};
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        color: ${colors.textSecondary};
    }

    .currency {
        position: absolute;
        left: 0;
        bottom: 0;
        transform: translateX(-100%);
        font-family: ${font.family.primary};
        font-size: 11px;
        font-weight: 500;
        line-height: 14px;
        color: ${colors.textPrimary};
    }
`;

export const PlanFeatureRow = styled.div`
    width: 100%;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textSecondary};
`;

export const TrialButton = styled.button<{$active: boolean}>`
    font-family: ${font.family.primary};
    border: 0;
    background: transparent;
    padding: 0;
    margin-top: 2px;

    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: ${colors.brandPrimary};
    text-align: center;

    &:hover {
        color: ${colors.brandDark};
        text-decoration: underline;
    }

    ${(p) =>
        p.$active &&
        css`
            color: ${colors.brandDark};
            text-decoration: underline;
        `}
`;

export const TrialCtaRow = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
`;

export const TrialCtaDescription = styled.div`
    display: inline-flex;
    width: fit-content;
    flex-direction: column;
    gap: 6px;
`;

export const TrialCtaInvoices = styled.div`
    display: inline-flex;
    width: fit-content;
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${colors.black};
`;

export const TrialCtaText = styled.div`
    display: inline-flex;
    width: fit-content;
    max-width: 230px;
    font-family: ${font.family.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.textSecondary};
`;

export const PlanFooter = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    height: 18px;
    margin-top: 10px;
`;
export const PlanFooterText = styled.div`
    font-family: ${font.family.primary};
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    color: ${colors.brandPrimary};
`;
